import React, {
	useCallback,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Files, Loader2Icon, ZoomIn, ZoomOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
	anchoredScroll,
	canvasPixelRatio,
	clampZoom,
	computeDocumentLayout,
	documentPointToPage,
	documentToScroll,
	findCurrentPageIndex,
	fitWidthZoom,
	formatZoom,
	isConstrainedDevice,
	LAYOUT,
	nextZoomStep,
	previousZoomStep,
	Size,
	viewportToDocument,
	ZOOM_STEPS,
} from './geometry';
import { PdfLoadStage, usePdfDocument } from './use-pdf-document';
import { describeError } from '@/utils/describe-error';
import { PdfViewerPage } from './pdf-viewer-page';
import { PdfThumbnails } from './pdf-thumbnails';
import { releaseCanvas, RenderCache } from './render-cache';

/** Delay between the last zoom change and re-rendering pages at the new scale. */
const RERENDER_DEBOUNCE_MS = 150;

export type PdfViewerHandle = {
	scrollToPage: (pageIndex: number) => void;
	/**
	 * Maps a viewport point (e.g. a pointer event's clientX/clientY) to page-relative
	 * base px, or null when it is not over a page.
	 */
	clientPointToPage: (
		clientX: number,
		clientY: number,
	) => { pageIndex: number; x: number; y: number } | null;
	getZoom: () => number;
	getPageSizes: () => Size[];
};

export type PdfViewerOverlayContext = {
	pageIndex: number;
	/** Page size in base px (96 dpi): the overlay's coordinate space. */
	size: Size;
	zoom: number;
};

/** `auto` fits the width but never zooms above 100 %. */
export type PdfViewerZoom = number | 'fit-width' | 'auto';

export type PdfViewerProps = {
	file: Blob | null | undefined;
	/** Content for each page's overlay layer, positioned in base px. */
	renderPageOverlay?: (context: PdfViewerOverlayContext) => React.ReactNode;
	initialZoom?: PdfViewerZoom;
	handleRef?: React.Ref<PdfViewerHandle>;
	onDocumentLoad?: (info: { numPages: number; pageSizes: Size[] }) => void;
	onCurrentPageChange?: (pageIndex: number) => void;
	onError?: (error: unknown) => void;
	className?: string;
	errorMessage?: React.ReactNode;
	/** `data-tour` for the thumbnails button (onboarding tours). */
	thumbnailsTourId?: string;
};

type ZoomMode = 'fit-width' | 'auto' | 'manual';

const LOAD_STAGE_LABELS: Record<PdfLoadStage, string> = {
	read: 'Error al leer el archivo',
	open: 'Error al abrir el PDF',
	pages: 'Error al leer las páginas',
};

const devicePixelRatio = () =>
	typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;

/** Touch-first device (phone, tablet): its primary pointer is a finger. */
const isCoarsePointer = () =>
	typeof window !== 'undefined' &&
	typeof window.matchMedia === 'function' &&
	window.matchMedia('(pointer: coarse)').matches;

export const PdfViewer = (props: PdfViewerProps) => {
	const { doc, pageSizes, isLoading, error, errorStage } = usePdfDocument(
		props.file,
	);
	const layout = useMemo(() => computeDocumentLayout(pageSizes), [pageSizes]);

	const scrollRef = useRef<HTMLDivElement>(null);
	const zoomContainerRef = useRef<HTMLDivElement>(null);

	const initialZoom = props.initialZoom ?? 'auto';
	const [zoomMode, setZoomMode] = useState<ZoomMode>(
		typeof initialZoom === 'number' ? 'manual' : initialZoom,
	);
	const [zoom, setZoom] = useState(
		typeof initialZoom === 'number' ? clampZoom(initialZoom) : 1,
	);
	// The zoom pages are rendered at; trails `zoom` by RERENDER_DEBOUNCE_MS.
	const [renderZoom, setRenderZoom] = useState(zoom);
	const zoomRef = useRef(zoom);
	const pendingScrollRef = useRef<{ left: number; top: number } | null>(null);

	const [viewportSize, setViewportSize] = useState<Size>({
		width: 0,
		height: 0,
	});
	const [nearPages, setNearPages] = useState<Set<number>>(() => new Set());
	const [currentPageIndex, setCurrentPageIndex] = useState(0);
	const [isThumbnailsOpen, setIsThumbnailsOpen] = useState(false);
	const [isZoomMenuOpen, setIsZoomMenuOpen] = useState(false);

	const renderCache = useMemo(
		() =>
			new RenderCache<HTMLCanvasElement>(
				6,
				// Phones and tablets get half the budget: 2^24 px, 64 MB.
				isConstrainedDevice(devicePixelRatio(), isCoarsePointer())
					? 2 ** 24
					: 2 ** 25,
				releaseCanvas,
			),
		[doc],
	);
	// Free the bitmaps of a replaced document or an unmounted viewer.
	useEffect(() => () => renderCache.clear(), [renderCache]);
	const thumbnailCache = useMemo(() => new Map<number, string>(), [doc]);

	// --- Notifications -------------------------------------------------------

	const { onDocumentLoad, onError, onCurrentPageChange } = props;
	useEffect(() => {
		if (doc) onDocumentLoad?.({ numPages: doc.numPages, pageSizes });
	}, [doc, pageSizes, onDocumentLoad]);

	useEffect(() => {
		if (error) onError?.(error);
	}, [error, onError]);

	useEffect(() => {
		onCurrentPageChange?.(currentPageIndex);
	}, [currentPageIndex, onCurrentPageChange]);

	// --- Zoom ----------------------------------------------------------------

	/**
	 * Changes zoom keeping the document point under `anchor` (viewport-relative px,
	 * default: viewport centre) where it is. Safe to call many times per frame.
	 */
	const requestZoom = useCallback(
		(
			target: number,
			anchor?: { x: number; y: number },
			mode: ZoomMode = 'manual',
		) => {
			const element = scrollRef.current;
			const oldZoom = zoomRef.current;
			const newZoom = clampZoom(target);
			setZoomMode(mode);
			if (!element || Math.abs(newZoom - oldZoom) < 1e-4) return;
			const point = anchor ?? {
				x: element.clientWidth / 2,
				y: element.clientHeight / 2,
			};
			const scroll = pendingScrollRef.current ?? {
				left: element.scrollLeft,
				top: element.scrollTop,
			};
			pendingScrollRef.current = {
				left: anchoredScroll({
					anchor: point.x,
					scroll: scroll.left,
					oldZoom,
					newZoom,
					contentSize: layout.width,
					viewportSize: element.clientWidth,
					center: true,
				}),
				top: anchoredScroll({
					anchor: point.y,
					scroll: scroll.top,
					oldZoom,
					newZoom,
					contentSize: layout.height,
					viewportSize: element.clientHeight,
				}),
			};
			zoomRef.current = newZoom;
			setZoom(newZoom);
		},
		[layout.width, layout.height],
	);

	/**
	 * Zooms to `target` with document point `point` (base px) placed under `anchor`
	 * (viewport-relative px). Pinch uses it with the point under the fingers at the
	 * start of the gesture, so the content follows the fingers (zoom and pan) and
	 * no rounding error builds up between touchmoves.
	 */
	const placeDocumentPoint = useCallback(
		(
			target: number,
			point: { x: number; y: number },
			anchor: { x: number; y: number },
		) => {
			const element = scrollRef.current;
			if (!element) return;
			const newZoom = clampZoom(target);
			setZoomMode('manual');
			const scroll = {
				left: documentToScroll({
					point: point.x,
					anchor: anchor.x,
					zoom: newZoom,
					contentSize: layout.width,
					viewportSize: element.clientWidth,
					center: true,
				}),
				top: documentToScroll({
					point: point.y,
					anchor: anchor.y,
					zoom: newZoom,
					contentSize: layout.height,
					viewportSize: element.clientHeight,
				}),
			};
			if (Math.abs(newZoom - zoomRef.current) < 1e-4) {
				// Same zoom: pan only. A zoom change still waiting to commit takes the
				// new scroll with it instead.
				if (pendingScrollRef.current) {
					pendingScrollRef.current = scroll;
				} else {
					element.scrollLeft = scroll.left;
					element.scrollTop = scroll.top;
				}
				return;
			}
			pendingScrollRef.current = scroll;
			zoomRef.current = newZoom;
			setZoom(newZoom);
		},
		[layout.width, layout.height],
	);

	useLayoutEffect(() => {
		const element = scrollRef.current;
		const pending = pendingScrollRef.current;
		if (element && pending) {
			element.scrollLeft = pending.left;
			element.scrollTop = pending.top;
		}
		pendingScrollRef.current = null;
	}, [zoom]);

	useEffect(() => {
		const timeout = window.setTimeout(
			() => setRenderZoom(zoom),
			RERENDER_DEBOUNCE_MS,
		);
		return () => window.clearTimeout(timeout);
	}, [zoom]);

	// Fit modes follow the viewport until the user picks a zoom themselves.
	useLayoutEffect(() => {
		if (zoomMode === 'manual' || !viewportSize.width || !layout.width)
			return;
		const fit = fitWidthZoom(viewportSize.width, layout.width);
		const target = zoomMode === 'auto' ? Math.min(1, fit) : fit;
		if (Math.abs(target - zoomRef.current) < 1e-4) return;
		zoomRef.current = target;
		setZoom(target);
		setRenderZoom(target);
	}, [zoomMode, viewportSize.width, layout.width]);

	// --- Viewport tracking ---------------------------------------------------

	useEffect(() => {
		const element = scrollRef.current;
		if (!element) return;
		const update = () =>
			setViewportSize({
				width: element.clientWidth,
				height: element.clientHeight,
			});
		update();
		if (typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(update);
		observer.observe(element);
		return () => observer.disconnect();
	}, [doc]);

	const updateCurrentPage = useCallback(() => {
		const element = scrollRef.current;
		if (!element || !layout.pages.length) return;
		const z = zoomRef.current;
		setCurrentPageIndex(
			findCurrentPageIndex(
				layout,
				element.scrollTop / z,
				element.clientHeight / z,
			),
		);
	}, [layout]);

	useEffect(() => {
		const element = scrollRef.current;
		if (!element) return;
		let frame = 0;
		const onScroll = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(updateCurrentPage);
		};
		element.addEventListener('scroll', onScroll, { passive: true });
		updateCurrentPage();
		return () => {
			cancelAnimationFrame(frame);
			element.removeEventListener('scroll', onScroll);
		};
	}, [updateCurrentPage, zoom]);

	// --- Virtualization ------------------------------------------------------

	const observerRef = useRef<IntersectionObserver | null>(null);
	const pageElementsRef = useRef(new Map<number, HTMLElement>());

	useEffect(() => {
		const root = scrollRef.current;
		if (!root || !doc) return;
		if (typeof IntersectionObserver === 'undefined') {
			// No observer (tests, very old browsers): keep the first pages live.
			setNearPages(new Set([0, 1, 2]));
			return;
		}
		const observer = new IntersectionObserver(
			(entries) =>
				setNearPages((current) => {
					const next = new Set(current);
					entries.forEach((entry) => {
						const index = Number(
							(entry.target as HTMLElement).dataset.pageIndex,
						);
						if (entry.isIntersecting) next.add(index);
						else next.delete(index);
					});
					return next;
				}),
			// One extra viewport above and below the visible one.
			{ root, rootMargin: '100% 0px 100% 0px' },
		);
		observerRef.current = observer;
		pageElementsRef.current.forEach((element) => observer.observe(element));
		return () => {
			observer.disconnect();
			observerRef.current = null;
		};
	}, [doc]);

	const registerPageElement = useCallback(
		(index: number, element: HTMLElement | null) => {
			const previous = pageElementsRef.current.get(index);
			if (previous && previous !== element) {
				observerRef.current?.unobserve(previous);
			}
			if (element) {
				pageElementsRef.current.set(index, element);
				observerRef.current?.observe(element);
			} else {
				pageElementsRef.current.delete(index);
			}
		},
		[],
	);

	// --- Gestures: Ctrl/Cmd + wheel and two-finger pinch ----------------------

	useEffect(() => {
		const element = scrollRef.current;
		if (!element) return;
		const viewportPoint = (clientX: number, clientY: number) => {
			const rect = element.getBoundingClientRect();
			return { x: clientX - rect.left, y: clientY - rect.top };
		};

		const onWheel = (event: WheelEvent) => {
			// Trackpad pinch arrives as a wheel event with ctrlKey set.
			if (!event.ctrlKey && !event.metaKey) return;
			event.preventDefault();
			const delta = Math.max(-50, Math.min(50, event.deltaY));
			requestZoom(
				zoomRef.current * Math.exp(-delta * 0.006),
				viewportPoint(event.clientX, event.clientY),
			);
		};

		// Touch events rather than pointer events: iOS Safari only lets us stop the
		// native page zoom reliably by cancelling a two-finger touchmove.
		let pinch: {
			distance: number;
			zoom: number;
			/** Document point (base px) under the fingers when the pinch started. */
			point: { x: number; y: number };
		} | null = null;
		const touchDistance = (touches: TouchList) =>
			Math.hypot(
				touches[0].clientX - touches[1].clientX,
				touches[0].clientY - touches[1].clientY,
			);
		const touchMidpoint = (touches: TouchList) =>
			viewportPoint(
				(touches[0].clientX + touches[1].clientX) / 2,
				(touches[0].clientY + touches[1].clientY) / 2,
			);
		const onTouchStart = (event: TouchEvent) => {
			if (event.touches.length !== 2) return;
			const zoom = zoomRef.current;
			const scroll = pendingScrollRef.current ?? {
				left: element.scrollLeft,
				top: element.scrollTop,
			};
			const mid = touchMidpoint(event.touches);
			pinch = {
				distance: touchDistance(event.touches),
				zoom,
				point: {
					x: viewportToDocument({
						anchor: mid.x,
						scroll: scroll.left,
						zoom,
						contentSize: layout.width,
						viewportSize: element.clientWidth,
						center: true,
					}),
					y: viewportToDocument({
						anchor: mid.y,
						scroll: scroll.top,
						zoom,
						contentSize: layout.height,
						viewportSize: element.clientHeight,
					}),
				},
			};
		};
		const onTouchMove = (event: TouchEvent) => {
			if (!pinch || event.touches.length !== 2) return;
			event.preventDefault();
			placeDocumentPoint(
				(pinch.zoom * touchDistance(event.touches)) / pinch.distance,
				pinch.point,
				touchMidpoint(event.touches),
			);
		};
		const onTouchEnd = (event: TouchEvent) => {
			if (event.touches.length < 2) pinch = null;
		};
		// iOS Safari's own pinch gesture events; cancelling them is a second guard
		// against the page zooming instead of the document.
		const onGesture = (event: Event) => event.preventDefault();

		element.addEventListener('wheel', onWheel, { passive: false });
		element.addEventListener('touchstart', onTouchStart, { passive: true });
		element.addEventListener('touchmove', onTouchMove, { passive: false });
		element.addEventListener('touchend', onTouchEnd);
		element.addEventListener('touchcancel', onTouchEnd);
		element.addEventListener('gesturestart', onGesture);
		element.addEventListener('gesturechange', onGesture);
		return () => {
			element.removeEventListener('wheel', onWheel);
			element.removeEventListener('touchstart', onTouchStart);
			element.removeEventListener('touchmove', onTouchMove);
			element.removeEventListener('touchend', onTouchEnd);
			element.removeEventListener('touchcancel', onTouchEnd);
			element.removeEventListener('gesturestart', onGesture);
			element.removeEventListener('gesturechange', onGesture);
		};
	}, [requestZoom, placeDocumentPoint, layout, doc]);

	// --- Imperative API ------------------------------------------------------

	const scrollToPage = useCallback(
		(pageIndex: number) => {
			const element = scrollRef.current;
			const page = layout.pages[pageIndex];
			if (!element || !page) return;
			element.scrollTop = Math.max(
				0,
				(page.top - LAYOUT.PADDING) * zoomRef.current,
			);
			setCurrentPageIndex(pageIndex);
		},
		[layout],
	);

	useImperativeHandle(
		props.handleRef,
		() => ({
			scrollToPage,
			clientPointToPage: (clientX, clientY) => {
				const container = zoomContainerRef.current;
				if (!container) return null;
				const rect = container.getBoundingClientRect();
				const z = zoomRef.current;
				return documentPointToPage(
					layout,
					(clientX - rect.left) / z,
					(clientY - rect.top) / z,
				);
			},
			getZoom: () => zoomRef.current,
			getPageSizes: () => pageSizes,
		}),
		[scrollToPage, layout, pageSizes],
	);

	// --- Render --------------------------------------------------------------

	const dpr = devicePixelRatio();
	const coarsePointer = isCoarsePointer();
	const { renderPageOverlay } = props;

	const zoomLabel = formatZoom(zoom);

	return (
		<div
			className={cn(
				'relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-[rgb(247,246,247)]',
				props.className,
			)}
		>
			<div
				ref={scrollRef}
				className="relative min-h-0 flex-1 overflow-auto"
				style={{ touchAction: 'pan-x pan-y' }}
				data-testid="pdf-viewer-scroll"
			>
				{doc && (
					<div
						ref={zoomContainerRef}
						data-testid="pdf-viewer-zoom-container"
						className="relative mx-auto"
						style={{
							width: layout.width * zoom,
							height: layout.height * zoom,
						}}
					>
						<div
							className="absolute top-0 left-0"
							style={{
								width: layout.width,
								height: layout.height,
								transform: `scale(${zoom})`,
								transformOrigin: '0 0',
							}}
						>
							{layout.pages.map((box) => (
								<PdfViewerPage
									key={box.index}
									doc={doc}
									box={box}
									pageCount={layout.pages.length}
									isNear={nearPages.has(box.index)}
									pixelRatio={canvasPixelRatio(
										box,
										renderZoom,
										dpr,
										{ isCoarsePointer: coarsePointer },
									)}
									cache={renderCache}
									registerElement={registerPageElement}
									onRenderError={onError}
									overlay={renderPageOverlay?.({
										pageIndex: box.index,
										size: {
											width: box.width,
											height: box.height,
										},
										zoom,
									})}
								/>
							))}
						</div>
					</div>
				)}
				{isLoading && (
					<div className="flex h-full min-h-[100px] items-center justify-center">
						<Loader2Icon className="animate-spin" />
					</div>
				)}
				{Boolean(error) && (
					<div
						className="flex h-full min-h-[100px] flex-col items-center justify-center gap-2 p-4 text-center text-sm text-[#666]"
						role="alert"
					>
						{props.errorMessage ??
							'No pudimos obtener el documento'}
						{/* The cause, readable on a phone without a developer console. */}
						<span
							className="max-w-full text-xs break-words text-[#999]"
							data-testid="pdf-viewer-error-detail"
						>
							{LOAD_STAGE_LABELS[errorStage ?? 'open']}:{' '}
							{describeError(error)}
						</span>
					</div>
				)}
			</div>

			{doc && isThumbnailsOpen && (
				<PdfThumbnails
					doc={doc}
					pageSizes={pageSizes}
					currentPageIndex={currentPageIndex}
					cache={thumbnailCache}
					onSelectPage={scrollToPage}
					onClose={() => setIsThumbnailsOpen(false)}
				/>
			)}

			{doc && (
				<div
					className={cn(
						'absolute right-3 bottom-3 z-10 flex items-center gap-1 rounded-lg border border-[#ddd] bg-white p-1 shadow-md',
						'md:flex-col',
					)}
					role="toolbar"
					aria-label="Controles del documento"
				>
					<ToolbarButton
						label="Ver páginas"
						dataTour={props.thumbnailsTourId}
						isActive={isThumbnailsOpen}
						onClick={() => setIsThumbnailsOpen((open) => !open)}
					>
						<Files size={18} />
					</ToolbarButton>
					<ToolbarButton
						label="Acercar"
						onClick={() => requestZoom(nextZoomStep(zoom))}
						disabled={nextZoomStep(zoom) === zoom}
					>
						<ZoomIn size={18} />
					</ToolbarButton>
					<div className="relative">
						<button
							type="button"
							className="h-8 min-w-[52px] rounded px-1 text-xs font-medium tabular-nums hover:bg-[#f0f0f0]"
							aria-label={`Zoom ${zoomLabel}`}
							aria-haspopup="menu"
							aria-expanded={isZoomMenuOpen}
							onClick={() => setIsZoomMenuOpen((open) => !open)}
						>
							{zoomLabel}
						</button>
						{isZoomMenuOpen && (
							<div
								role="menu"
								className="absolute right-0 bottom-full mb-1 flex w-36 flex-col rounded-md border border-[#ddd] bg-white py-1 text-sm shadow-lg md:right-full md:bottom-0 md:mr-1 md:mb-0"
							>
								{[...ZOOM_STEPS].reverse().map((step) => (
									<ZoomMenuItem
										key={step}
										label={formatZoom(step)}
										isActive={
											zoomMode === 'manual' &&
											Math.abs(step - zoom) < 1e-3
										}
										onSelect={() => {
											requestZoom(step);
											setIsZoomMenuOpen(false);
										}}
									/>
								))}
								<ZoomMenuItem
									label="Ajustar al ancho"
									isActive={zoomMode === 'fit-width'}
									onSelect={() => {
										const element = scrollRef.current;
										if (element) {
											requestZoom(
												fitWidthZoom(
													element.clientWidth,
													layout.width,
												),
												undefined,
												'fit-width',
											);
										}
										setIsZoomMenuOpen(false);
									}}
								/>
							</div>
						)}
					</div>
					<ToolbarButton
						label="Alejar"
						onClick={() => requestZoom(previousZoomStep(zoom))}
						disabled={previousZoomStep(zoom) === zoom}
					>
						<ZoomOut size={18} />
					</ToolbarButton>
				</div>
			)}
		</div>
	);
};

const ToolbarButton = (props: {
	label: string;
	onClick: () => void;
	disabled?: boolean;
	isActive?: boolean;
	dataTour?: string;
	children: React.ReactNode;
}) => (
	<button
		type="button"
		aria-label={props.label}
		data-tour={props.dataTour}
		title={props.label}
		disabled={props.disabled}
		onClick={props.onClick}
		className={cn(
			'flex h-8 w-8 items-center justify-center rounded text-[#333] hover:bg-[#f0f0f0] disabled:opacity-40 disabled:hover:bg-transparent',
			props.isActive && 'bg-[#ececec]',
		)}
	>
		{props.children}
	</button>
);

const ZoomMenuItem = (props: {
	label: string;
	isActive: boolean;
	onSelect: () => void;
}) => (
	<button
		type="button"
		role="menuitemradio"
		aria-checked={props.isActive}
		onClick={props.onSelect}
		className={cn(
			'px-3 py-1.5 text-left hover:bg-[#f0f0f0]',
			props.isActive && 'font-semibold',
		)}
	>
		{props.label}
	</button>
);
